import { FC } from 'react';
import Modal from '@new/common/ui/Modal/Modal';
import { SectionHeading } from '@new/common/ui/SectionHeading/SectionHeading';
import { DescriptionList } from '@new/common/ui/DescriptionList/DescriptionList';
import { TranscriptStatusBadge } from '@new/features/transcripts/shared/components/StatusBadge';
import { TranscriptFilesList } from '@new/features/transcripts/shared/components/TranscriptFilesList';
import { TranscriptBreakdown } from '@teachur/http-client';

const DEFAULT_EVALUATION_CRITERIA =
  'Evaluation considers accreditation, credential(s) earned (if any), college-level credits with grades of C- or higher, and applicable credit recommendations from recognized organizations.';

interface TranscriptDetailsModalProps {
  transcript: TranscriptBreakdown | null;
  onClose: () => void;
}

export const TranscriptDetailsModal: FC<TranscriptDetailsModalProps> = ({
  transcript,
  onClose,
}) => {
  if (!transcript) return null;
  const decided = transcript.status === 'approved' || transcript.status === 'rejected';

  return (
    <Modal isOpen={!!transcript} onClose={onClose} title={transcript.institution}>
      <div className="flex h-full min-h-0 flex-1 flex-col gap-5 overflow-y-auto">
        <div className="shrink-0 overflow-hidden rounded-lg border border-(--neutral-black-NB200)">
          <div className="px-4 pt-4">
            <SectionHeading title="Faculty review" />
          </div>
          <DescriptionList>
            <DescriptionList.Row label="Result">
              <TranscriptStatusBadge status={transcript.status} />
            </DescriptionList.Row>
            <DescriptionList.Row label="Official credits">
              {transcript.officialCredits != null ? transcript.officialCredits : '—'}
            </DescriptionList.Row>
            <DescriptionList.Row label="Unofficial credits">
              {transcript.unofficialCredits}
            </DescriptionList.Row>
            {decided ? (
              <DescriptionList.Row label="Reviewer note">
                <div className="flex flex-col gap-1">
                  {transcript.reviewNote ? <span>{transcript.reviewNote}</span> : null}
                  <span>{DEFAULT_EVALUATION_CRITERIA}</span>
                  <span className="text-(--neutral-black-NB600)!">
                    Questions? Review{' '}
                    <a
                      className="text-(--primary-O1000)!"
                      href="https://newlane.edu/transferring-credits-toward-an-aa-or-ba-degree/"
                    >
                      how transfer credit works at Newlane
                    </a>
                    , and/or attend an upcoming{' '}
                    <a
                      className="text-(--primary-O1000)!"
                      href="https://us06web.zoom.us/meeting/register/2YfGon5tSL-Pp91vqM7Vqg#/registration"
                    >
                      Transfer Credit Workshop
                    </a>{' '}
                    (held weekly).
                  </span>
                </div>
              </DescriptionList.Row>
            ) : null}
          </DescriptionList>
        </div>

        <div className="flex flex-col gap-2">
          <h4 className="m-0! [font-size:var(--fs-p2)]! font-semibold text-(--neutral-black-NB1000)!">
            Documents
          </h4>
          <TranscriptFilesList
            files={(transcript.files ?? []).filter((f) => f.kind !== 'legacy')}
          />
        </div>

        <div className="flex flex-col gap-2">
          <h4 className="m-0! [font-size:var(--fs-p2)]! font-semibold text-(--neutral-black-NB1000)!">
            Courses
          </h4>

          {transcript.courses.length === 0 ? (
            <p className="[font-size:var(--fs-p4)]! text-(--neutral-black-NB700)!">
              No courses recorded for this transcript.
            </p>
          ) : (
            <div className="rounded-md border border-(--neutral-black-NB200)">
              <div>
                <table className="w-full border-collapse [font-size:var(--fs-p4)]!">
                  <thead className="sticky top-0 z-10 bg-(--neutral-white-NW900)">
                    <tr className="text-left text-(--neutral-black-NB700)!">
                      <th className="px-4 py-2.5 font-semibold">Course</th>
                      <th className="w-24 px-4 py-2.5 text-center font-semibold">Grade</th>
                      <th className="w-24 px-4 py-2.5 text-center font-semibold">Credit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transcript.courses.map((c) => (
                      <tr
                        key={c.id}
                        className="border-t border-(--neutral-black-NB100) text-(--neutral-black-NB1000)!"
                      >
                        <td className="px-4 py-2.5">
                          {c.rawCode ? (
                            <span className="text-(--neutral-black-NB600)!">{c.rawCode} </span>
                          ) : null}
                          {c.rawName}
                        </td>
                        <td className="px-4 py-2.5 text-center">{c.grade ?? '—'}</td>
                        <td className="px-4 py-2.5 text-center">
                          {c.creditHours != null ? Math.round(Number(c.creditHours)) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};
